import { ReactNode } from 'react'
import styled from 'styled-components'
import { Trans, useTranslation } from 'react-i18next'
import { theme } from '../../../../theme/theme'
import { printCardOutline } from '../../../../theme/print'
import { BodyLarge, BodySmall, BodyTiny, Bold, Title2 } from '../../../styles/Typography'
import { NotEnoughFollowUpData } from '../../../charts/NotEnoughData'
import { SummaryMetrics } from '../../../../utils/statistics/types'
import { getHighlightPcts, isSummaryTileVisible } from '../../../../utils/statistics/summary'

type Props = {
  metrics: SummaryMetrics
  action?: ReactNode
}

export const SummarySection = ({ metrics, action }: Props) => {
  const { t } = useTranslation()

  const tiles = [
    { title: t('admin.statistics.summaryImprovedWorkAbility'), metric: metrics.improvedWorkAbility },
    { title: t('admin.statistics.summaryLifestyleChange'), metric: metrics.lifestyleChange },
    { title: t('admin.statistics.summaryDecreasedSymptoms'), metric: metrics.decreasedSymptoms3m, highlight: true },
  ]

  const visibleTiles = tiles.filter(({ metric }) => isSummaryTileVisible(metric))
  const highlightPcts = getHighlightPcts(metrics)

  return (
    <Section>
      <SummaryTitle>{t('admin.statistics.summaryTitle')}</SummaryTitle>
      <SummaryDescription>{t('admin.statistics.summaryDescription')}</SummaryDescription>
      <HighlightRow>
        <HighlightBox>
          <HighlightText>
            {highlightPcts ? (
              <Trans
                i18nKey="admin.statistics.summaryHighlight"
                values={highlightPcts}
                components={{ bold: <Bold /> }}
              />
            ) : (
              t('admin.statistics.summaryHighlightIntro')
            )}
          </HighlightText>
        </HighlightBox>
        {action && <ActionSlot>{action}</ActionSlot>}
      </HighlightRow>
      {visibleTiles.length > 0 && (
        <TileGrid $columns={visibleTiles.length}>
          {visibleTiles.map(({ title, metric, highlight }) => (
            <Tile key={title} $highlight={!!highlight}>
              <TileTitle $highlight={!!highlight}>{title}</TileTitle>
              {metric ? (
                <TileValue $highlight={!!highlight}>
                  {t('admin.statistics.summaryPct', { pct: Math.round(metric.pct) })}
                </TileValue>
              ) : (
                <NotEnoughFollowUpData variant={highlight ? 'tileBoxedHighlight' : 'tileBoxed'} />
              )}
            </Tile>
          ))}
        </TileGrid>
      )}
      <BasedOn>{t('admin.statistics.summaryBasedOn')}</BasedOn>
    </Section>
  )
}

const SummaryTitle = styled(Title2)`
  color: ${theme.color.plum};
  margin-bottom: ${theme.spacing.medium}px;
`

const SummaryDescription = styled(BodyLarge)`
  color: ${theme.color.blueDark};
  margin-bottom: ${theme.spacing.mediumLarge}px;
`

const Section = styled.section`
  margin-bottom: ${theme.spacing.xxlarge}px;

  @media print {
    margin-bottom: 0;
  }
`

const HighlightRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.medium}px;
  margin-bottom: ${theme.spacing.xxlarge}px;

  @media print {
    margin-bottom: ${theme.spacing.medium}px;
  }

  @media screen and (max-width: ${theme.breakpoint.large}px) {
    margin-bottom: ${theme.spacing.medium}px;
  }

  @media screen and (max-width: ${theme.breakpoint.medium}px) {
    flex-direction: column;
    align-items: stretch;
  }
`

const HighlightBox = styled.div`
  background: ${theme.color.white};
  border-radius: ${theme.spacing.small}px;
  padding: ${theme.spacing.medium}px;
  max-width: 640px;

  @media print {
    background: ${theme.color.plumLight};
  }
`

const ActionSlot = styled.div`
  margin-left: auto;
`

const HighlightText = styled(BodySmall)`
  color: ${theme.color.plum};
`

const TileGrid = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns}, 1fr);
  gap: ${theme.spacing.medium}px;
  margin-bottom: ${theme.spacing.small}px;

  @media screen and (max-width: ${theme.breakpoint.large}px) {
    grid-template-columns: repeat(${({ $columns }) => Math.min($columns, 2)}, 1fr);
  }

  @media screen and (max-width: ${theme.breakpoint.medium}px) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.small}px;
  }
`

const Tile = styled.div<{ $highlight: boolean }>`
  background: ${({ $highlight }) => ($highlight ? theme.color.plum : theme.color.white)};
  border-radius: ${theme.spacing.small}px;
  padding: ${theme.spacing.mediumLarge}px;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.medium}px;
  box-shadow:
    0px 1px 2px 0px rgba(0, 0, 0, 0.06),
    0px 3px 9px 0px rgba(0, 0, 0, 0.05);
  ${printCardOutline}

  @media print {
    border-color: ${({ $highlight }) => ($highlight ? theme.color.plum : theme.color.beigeDark)};
  }
`

const TileTitle = styled(BodySmall)<{ $highlight: boolean }>`
  color: ${({ $highlight }) => ($highlight ? theme.color.orange : theme.color.black)};
`

const TileValue = styled.span<{ $highlight: boolean }>`
  font-family: ${theme.font.heading};
  font-size: ${theme.fontSize.jumbo}px;
  font-weight: 500;
  line-height: 1;
  color: ${({ $highlight }) => ($highlight ? theme.color.beige : theme.color.plum)};
`

const BasedOn = styled(BodyTiny)`
  color: ${theme.color.grey5};
`
